import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { GET_TINYCMS_ARTICLE_ADDRESS_REPAIRS } from '../../graphql/queries';
import { FIX_TINYCMS_ARTICLE_ADDRESS } from '../../graphql/mutations';
import { InkSection } from './shared';

/**
 * Website › Manage › Articles: article addresses with a stray number left by a
 * curly quote or dash in a feed title (/article/spiral-8217-s-edge, backlog
 * 0f60ed04). One click fixes the working copy; the address changes when the
 * article is approved, and the old one then redirects. Nothing shows when
 * there's nothing to fix.
 */

type AddressRepair = {
  id: string;
  title: string;
  live: boolean;
  state: 'fix' | 'waiting';
  address: string;
  newAddress: string;
};
type AddressRepairs = { items: AddressRepair[]; total: number; toFix: number };

export function useArticleAddressRepairs() {
  const { accountId } = useAccount();
  const q = useQuery<{ tinycmsArticleAddressRepairs: AddressRepairs }>(
    GET_TINYCMS_ARTICLE_ADDRESS_REPAIRS,
    { variables: { accountId }, skip: !accountId, fetchPolicy: 'cache-and-network' }
  );
  return q.data?.tinycmsArticleAddressRepairs || null;
}

export function ArticleAddressRepairs() {
  const { accountId } = useAccount();
  const repairs = useArticleAddressRepairs();
  const [problem, setProblem] = useState<string | null>(null);
  const [fix, { loading }] = useMutation(FIX_TINYCMS_ARTICLE_ADDRESS, {
    update(cache, { data }) {
      cache.writeQuery({
        query: GET_TINYCMS_ARTICLE_ADDRESS_REPAIRS,
        variables: { accountId },
        data: { tinycmsArticleAddressRepairs: data.fixTinycmsArticleAddress },
      });
    },
  });
  if (!repairs?.items.length) return null;

  const onFix = async (id: string) => {
    setProblem(null);
    try {
      await fix({ variables: { accountId, id } });
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    }
  };

  return (
    <InkSection
      id="addresses"
      title="Web addresses to fix"
      description="These addresses have a stray number, left by a curly quote or dash in the title. A fix waits in To approve. Once it’s approved, the old address sends visitors to the new one."
    >
      <div className="ink-site-card">
        {repairs.items.map((item) => (
          <div className="ink-site-row" key={item.id}>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span className="ink-site-row-title">{item.title}</span>
              <span
                className="ink-site-row-desc"
                style={{ display: 'block', overflowWrap: 'anywhere' }}
              >
                {item.live ? null : 'Not live yet: '}
                {item.address} → {item.newAddress}
              </span>
            </span>
            {item.state === 'fix' ? (
              <button
                type="button"
                className="ink-btn ink-btn-quiet"
                disabled={loading}
                aria-label={`Fix the address of ${item.title}`}
                onClick={() => onFix(item.id)}
              >
                Fix address
              </button>
            ) : (
              <Link to={`/content/${item.id}`} className="ink-site-open">
                To approve →
              </Link>
            )}
          </div>
        ))}
        {repairs.total > repairs.items.length ? (
          <div className="ink-site-row">
            <span className="ink-site-row-desc">
              Showing {repairs.items.length} of {repairs.total}. The rest appear as these are approved.
            </span>
          </div>
        ) : null}
      </div>
      {problem ? (
        <p className="ink-site-row-desc" role="alert" style={{ marginTop: 8 }}>
          {problem}
        </p>
      ) : null}
    </InkSection>
  );
}
